import crWordmarkWhite from "@/assets/cipher-row-wordmark-white.svg";
/**
 * LandingNav · shared top nav for marketing pages (/verify, /docs,
 * /blog, /book-demo, etc). Matches the inline nav in src/pages/Index.tsx.
 *
 * Desktop row (2026-08-02): Pricing · Coverage · Capabilities ▾ · About ·
 * Live Demo, then the "Cipher & Row" wordmark on the left and the theme /
 * language / Sign in / Start free cluster on the right. Responsive with a
 * mobile menu toggle that carries every link and CTA.
 *
 * CAPABILITIES MENU: Packet Scanner, Verify and MCP were three separate
 * top-level items; they now live in a dropdown to the right of Coverage
 * (see CapabilitiesMenu below). The mint glow that used to mark Verify moved
 * with the row's remaining secondary CTA — Live Demo — so the header still
 * has exactly one glowing pill.
 *
 * FLOATING SHELL (2026-08-02): the bar is no longer edge-to-edge. A
 * click-through wrapper supplies the inset from the page top/sides and the
 * <nav> itself is a rounded card with a full border and elevation
 * (.cr-nav-floating in index.css). Inset applies on mobile too — same
 * design, smaller gutters.
 *
 * 🔑 ABSOLUTE, NOT FIXED (founder call 2026-08-02): the header is pinned to
 * the TOP OF THE PAGE and scrolls away with the content — it must not track
 * the viewport. `absolute` (not `sticky`, not `fixed`) is what does that
 * while staying out of flow, so the pt-24/pt-28 the marketing pages already
 * use to clear the header keeps working unchanged. It resolves against each
 * page's root wrapper, every one of which starts at y=0 (some are
 * `relative`, e.g. PricingPage — harmless, same origin); CinematicRouteFrame
 * is a pass-through fragment, so no transform creates a stray containing
 * block. If you add a positioned/transformed wrapper that does NOT start at
 * the page top, this header will anchor to it — verify in a browser.
 * There is deliberately no elevate-on-scroll state: the card is gone from
 * view by the time you have scrolled, so it would only ever flash.
 *
 * 🔑 HEIGHT BUDGET: marketing pages clear the header with pt-24 (96px) /
 * pt-28, so the gutter + card must stay inside 96px. Measured in Chromium
 * 2026-08-02 — card BOTTOM edge: 78px under 640, 86px from 640-1279 (the
 * 44px hamburger is the tallest child there, not the logo), 82px at 1280+.
 * Worst case leaves 10px of clearance. Growing the gutter or the card padding
 * eats it — re-measure and re-check the page paddings before you do.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Search,
  Sun,
  Moon,
  Play,
  ChevronDown,
  ScanLine,
  Plug,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnime } from "@/lib/anime/useAnime";
import { DURATION, EASE } from "@/lib/anime/motion-vocab";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CinematicLink } from "@/components/landing/CinematicLink";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { supabase } from "@/integrations/supabase/client";

interface CapabilityItem {
  to: string;
  label: string;
  blurb: string;
  Icon: LucideIcon;
}

/**
 * The three product surfaces, grouped under "Capabilities" (founder call
 * 2026-08-02). Order is deliberate and shared by the desktop dropdown and the
 * mobile section — Packet Scanner, then Verify, then MCP.
 *
 * Blurbs describe what each door actually does today; keep them aligned with
 * the shipped feature set (CLAUDE.md, "Sell only what ships") rather than the
 * roadmap.
 */
function capabilityItems(verifyLabel: string): CapabilityItem[] {
  return [
    {
      to: "/product/packet-scanner",
      label: "Packet Scanner",
      blurb: "Read a packet, flag what's off",
      Icon: ScanLine,
    },
    {
      to: "/verify",
      label: verifyLabel,
      blurb: "Free carrier and broker lookup",
      Icon: Search,
    },
    {
      to: "/mcp",
      label: "MCP",
      blurb: "Give your AI agent the CR tools",
      Icon: Plug,
    },
  ];
}

/**
 * Desktop "Capabilities" dropdown · sits between Coverage and About.
 *
 * Deliberately NOT the shadcn DropdownMenu: that portals to <body> and dresses
 * itself in the shadcn `--popover` tokens, which are the app's dark values on
 * every marketing route. This panel renders inline inside [data-cr-nav] so the
 * marketing --cr-* tokens (and the light/dark flip) reach it for free.
 *
 * Opens on hover for mice and on click/ArrowDown for keyboards and touch. The
 * panel's wrapper carries the pt-2 offset rather than a margin so the gap
 * between trigger and card is INSIDE the hover region — a real margin there
 * fires mouseleave as the pointer crosses it and the menu shuts under you.
 */
function CapabilitiesMenu({
  items,
  isActive,
}: {
  items: CapabilityItem[];
  isActive: (to: string) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // True while THIS open was caused by a mouse hovering in — see the click
  // handler for why that has to be distinguishable from a click-open.
  const openedByHover = useRef(false);
  const { pathname } = useLocation();
  const sectionActive = items.some((item) => isActive(item.to));

  // A navigation that leaves the panel mounted would overlay the new page.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="relative"
      // pointerType-gated, not onMouseEnter: a touch tap also synthesises
      // mouseenter, which would open the panel and then have the click that
      // follows shut it again.
      onPointerEnter={(e) => {
        if (e.pointerType !== "mouse") return;
        openedByHover.current = true;
        setOpen(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType !== "mouse") return;
        openedByHover.current = false;
        setOpen(false);
      }}
      // Tabbing past the last item closes the menu; focus moving BETWEEN the
      // trigger and the items stays inside currentTarget and keeps it open.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="landing-capabilities-menu"
        data-active={sectionActive ? "true" : undefined}
        // A mouse click on the trigger arrives AFTER the pointerenter that
        // already opened the panel, so a plain toggle would shut the menu the
        // instant you clicked the thing you were hovering — and it would stay
        // shut, because no second pointerenter is coming while the cursor sits
        // there. Swallow that first click; a further click still closes.
        onClick={() => {
          if (openedByHover.current) {
            openedByHover.current = false;
            return;
          }
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            // The panel mounts on the next paint, so defer the focus move.
            requestAnimationFrame(() =>
              panelRef.current?.querySelector<HTMLElement>("a")?.focus(),
            );
          }
        }}
        className={`cr-nav-link inline-flex items-center gap-1 text-sm ${
          sectionActive ? "font-semibold" : ""
        }`}
      >
        Capabilities
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          id="landing-capabilities-menu"
          ref={panelRef}
          className="absolute left-0 top-full z-50 w-[288px] pt-2"
        >
          <div className="cr-nav-menu rounded-xl border p-1.5">
            {items.map((item) => (
              <CinematicLink
                key={item.to}
                to={item.to}
                aria-current={isActive(item.to) ? "page" : undefined}
                onClick={() => setOpen(false)}
                className="cr-nav-menu-item flex items-start gap-2.5 rounded-lg px-2.5 py-2 transition-colors"
              >
                <item.Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium leading-tight">{item.label}</span>
                  <span className="cr-nav-menu-blurb mt-0.5 block text-xs leading-snug">
                    {item.blurb}
                  </span>
                </span>
              </CinematicLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function LandingNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  // Auth-aware CTA: a signed-in user sees "Dashboard" (→ /dash), anonymous
  // users see "Sign in"/"Start free". Subscribed directly to supabase auth so
  // the nav works on marketing pages regardless of whether an AuthProvider
  // wraps them. The direct /dash link is safe again (founder decision
  // 2026-07-18): useCinematicNavigate plain-navigates EVERY route since the
  // dead-click fix, so the 2026-05-18 dead cinematic path cannot recur.
  const [hasSession, setHasSession] = useState(false);
  useEffect(() => {
    let mounted = true;
    // Every auth touch is guarded: the marketing nav must render (logged-out
    // shape) even if the auth client is unavailable or partially stubbed.
    try {
      supabase.auth
        .getSession()
        .then(({ data }) => {
          if (mounted) setHasSession(!!data?.session);
        })
        .catch(() => {
          /* session probe failing must never break the nav */
        });
    } catch {
      /* ditto */
    }
    let unsubscribe: (() => void) | null = null;
    try {
      const res = supabase.auth.onAuthStateChange((_event, session) => {
        setHasSession(!!session);
      });
      unsubscribe = () => res.data.subscription.unsubscribe();
    } catch {
      /* ditto */
    }
    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, []);
  // Marketing theme (light default on the homepage). The nav flips via the
  // --cr-* tokens on the [data-cr-nav] elements; the toggle is only shown on
  // routes that actually participate in theming (currently the homepage).
  const { theme, isThemed, toggle } = useSiteTheme();
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // a11y: Escape closes the menu and returns focus to the toggle.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    // a11y: move focus into the menu (first link) when it opens.
    mobileMenuRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileOpen]);

  const isActive = (to: string) =>
    pathname === to || pathname.startsWith(to + "/");

  // Colors + hover/active come from the [data-cr-nav] .cr-nav-link rules in
  // index.css so the links flip with the theme; only the active weight stays
  // in JS.
  const mobileLinkClass = (to: string) =>
    `block text-[15px] py-2 cr-nav-link ${isActive(to) ? "font-semibold" : ""}`;

  const desktopLinkClass = (to: string) =>
    `text-sm cr-nav-link ${isActive(to) ? "font-semibold" : ""}`;

  // One list, two renderings: the desktop dropdown and the mobile section.
  const capabilities = capabilityItems(t("nav.verifyCarrier"));

  const { ref: navRef } = useAnime<HTMLElement>({
    opacity: [0, 1],
    translateY: [-16, 0],
    duration: DURATION.base,
    ease: EASE.soft,
    autoPlay: true,
  });


  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Skip to content
      </a>
      {/* Wrapper · owns the float gutter only, and anchors the header to the top
          of the PAGE (absolute, see the note in the file header) so it scrolls
          away instead of tracking the viewport. pointer-events-none so the
          transparent margin around the card never swallows clicks meant for the
          hero underneath; the card and the mobile sheet re-enable them. */}
      <div className="absolute top-0 left-0 right-0 z-50 px-3 pt-3 sm:px-6 sm:pt-4 lg:px-8 pointer-events-none">
        <nav
          ref={navRef}
          style={{ opacity: 0 }}
          data-cr-nav
          className="cr-nav-floating pointer-events-auto mx-auto flex w-full max-w-[1440px] items-center justify-between gap-2 rounded-2xl border px-3 py-2.5 backdrop-blur-[14px] sm:px-5 sm:py-3"
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Logo · the clean transparent white lockup, inverted to black on the
              light theme (the standalone black wordmark ships with a baked-in
              white box, so we invert instead of swapping assets). */}
          <Link
            to="/"
            data-cr-brand-logo
            className="flex items-center"
          >
            <img
              src={crWordmarkWhite}
              alt="Cipher & Row"
              width={160}
              height={40}
              loading="eager"
              decoding="sync"
              {...({ fetchpriority: "high" } as Record<string, string>)}
              /* object-LEFT, not just object-contain. The asset is 880×360
                 (2.44:1) but the box these attrs declare is 160×40 (4:1), so
                 contain letterboxes the artwork and centring parked ~36px of
                 empty pixels between the card's padding and the first ink —
                 the logo read as floating away from the edge, worst on mobile
                 where the padding is only 12px. Aligning left makes the
                 rendered gap equal the actual padding. The box stays 160 wide
                 so the reserved space (and CLS behaviour) is unchanged. */
              className="h-9 sm:h-10 shrink-0 object-contain object-left"
              style={theme === "light" ? { filter: "invert(1)" } : undefined}
            />
          </Link>

          {/* Desktop nav links.

              BREAKPOINT: the row appears at xl (1280px), not md. Measured
              2026-08-01: five links plus the Live Demo and Verify pills plus the
              right-hand cluster need ~1130px of content box, so anywhere under
              ~1090px the row overflowed its container and pushed "Start free" off
              the right edge (worst at 768-1000px, where it was clipped outright).
              lg was tried first and fits with 0px to spare at exactly 1024 —
              no room for a longer translated label — so the switch is xl, which
              leaves ~90px of slack. Everything below xl gets the mobile menu,
              which carries every link and CTA in this row.
              ⚠ The 2026-08-02 Capabilities grouping took this row from seven
              items to five and bought back real width, but the breakpoint STAYS
              at xl until someone re-measures — fr-CA is the binding case (see
              the note below) and it was already wrapping at 1280 with the old
              seven, so a narrower cutoff is not free just because English fits.
              Landing CTAs are plain instant navigation (user decision
              2026-05-18, shipped 065c935b). No per-CTA variant props —
              `useCinematicNavigate` short-circuits landing routes to a
              plain navigate (the dead globe-dolly was removed).

              GAP: 5 (20px), down from 8 (32px). The floating shell spends
              ~42px of horizontal budget the old edge-to-edge bar did not
              (16px gutter + 20px card padding, per side), and at gap-8 the row
              overflowed its container by 48px in French at 1280. Six gaps ×
              12px buys that back with room to spare — measured headroom
              between this row and the CTA cluster is 59px at 1280 and 171px
              at 1536+, in both locales' worst case.
              ⚠ FRENCH still wraps this row to two lines (84px card vs 66px in
              English). That is PRE-EXISTING, not from this redesign: `main`
              wraps too, and worse — 91px, measured both 2026-08-02. The row
              genuinely cannot fit the fr-CA labels at any width here; the real
              fix is collapsing it to the hamburger on a measured fit check,
              which is its own change. */}
          <div className="hidden xl:flex items-center gap-5">
            <CinematicLink
              to="/pricing"
              aria-current={isActive("/pricing") ? "page" : undefined}
              className={desktopLinkClass("/pricing")}
            >
              {t("nav.pricing")}
            </CinematicLink>
            <CinematicLink
              to="/coverage"
              aria-current={isActive("/coverage") ? "page" : undefined}
              className={desktopLinkClass("/coverage")}
            >
              {t("nav.coverage")}
            </CinematicLink>
            {/* Capabilities · the three product surfaces, collapsed out of the
                top level (2026-08-02). Packet Scanner / Verify / MCP each used
                to be their own item, which is what made this row overflow at
                every width below xl. */}
            <CapabilitiesMenu items={capabilities} isActive={isActive} />
            <CinematicLink
              to="/about"
              aria-current={isActive("/about") ? "page" : undefined}
              className={desktopLinkClass("/about")}
            >
              {t("nav.about")}
            </CinematicLink>
            {/* Simulated Packet Scanner demo (public 2026-07-26, founder flip).
                It inherits the mint glow that used to sit on Verify — Verify
                moved into the Capabilities menu, and the row still wants
                exactly one glowing secondary CTA next to the solid "Start
                free". Every colour comes from .cr-nav-demo (index.css) so the
                pill re-tones itself on the light theme instead of wearing the
                dark theme's mint and bloom on white. */}
            <CinematicLink
              to="/demo/scanner"
              aria-current={isActive("/demo/scanner") ? "page" : undefined}
              className="cr-nav-demo inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 py-2 text-sm font-semibold transition-all duration-200"
            >
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
              {t("nav.liveDemo")}
            </CinematicLink>
          </div>

          {/* Desktop CTAs.

              BREAKPOINT: md (768px), deliberately NOT xl like the links row above.
              The 2026-08-01 overflow fix moved the LINKS to xl, which was right —
              five links plus two pills genuinely do not fit under ~1090px. But the
              CTAs were inside the same `hidden xl:flex` block, so they collapsed
              into the hamburger too, and between 1024px and 1280px the header lost
              its only conversion action.
              🔑 That window is not hypothetical: a 1280x800 laptop lands at a
              ~1283px viewport maximised — THREE pixels above the cliff — so any
              un-maximise, devtools pane, or zoom step hides "Start free" entirely.
              Links can collapse to the hamburger; the sign-up path must not.
              Below md the mobile menu still carries everything. */}
          <div className="hidden md:flex items-center gap-3">
            {isThemed && (
              <button
                type="button"
                onClick={toggle}
                aria-label={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
                className="cr-nav-toggle inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors"
              >
                {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
            )}
            <LanguageSwitcher variant="icon" />
            {hasSession ? (
              // Signed-in users see "Dashboard" -> /dash (founder decision
              // 2026-07-18). Safe now: useCinematicNavigate plain-navigates
              // EVERY route since the dead-click fix, so the old reason for
              // routing through /login no longer applies.
              <CinematicLink
                to="/dash"
                aria-current={isActive("/dash") ? "page" : undefined}
                className={desktopLinkClass("/dash")}
              >
                {t("nav.dashboard", "Dashboard")}
              </CinematicLink>
            ) : (
              <>
                <CinematicLink
                  to="/login"
                  aria-current={isActive("/login") ? "page" : undefined}
                  className={desktopLinkClass("/login")}
                >
                  {t("nav.signIn")}
                </CinematicLink>
                <CinematicLink
                  to="/signup"
                  className="cr-nav-cta px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  {t("nav.startFree")}
                </CinematicLink>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            ref={toggleRef}
            type="button"
            className="cr-nav-link xl:hidden min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            aria-controls="landing-mobile-menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>

        {/* Mobile menu · a second floating panel sharing the header's gutters
            and hung 8px below the card. It lives INSIDE the wrapper and in
            normal flow (mt-2, not fixed) so it travels with the header now
            that the header scrolls away — a viewport-pinned sheet would
            detach from the card the moment the page is not at scroll 0.
            Body scroll is locked while it is open, so it can only be opened
            while the header is on screen; max-h keeps a long list scrolling
            inside the panel rather than running off the bottom. */}
        {mobileOpen && (
          <div
            id="landing-mobile-menu"
            ref={mobileMenuRef}
            data-cr-nav
            className="cr-nav-mobile cr-nav-floating pointer-events-auto xl:hidden mt-2 mx-auto w-full max-w-[1440px] max-h-[calc(100svh-98px)] sm:max-h-[calc(100svh-110px)] overflow-y-auto overscroll-contain rounded-2xl border p-6 space-y-4 text-center"
          >
            <CinematicLink
              to="/pricing"
              onClick={() => setMobileOpen(false)}
              aria-current={isActive("/pricing") ? "page" : undefined}
              className={mobileLinkClass("/pricing")}
            >
              {t("nav.pricing")}
            </CinematicLink>
            <CinematicLink
              to="/coverage"
              onClick={() => setMobileOpen(false)}
              aria-current={isActive("/coverage") ? "page" : undefined}
              className={mobileLinkClass("/coverage")}
            >
              {t("nav.coverage")}
            </CinematicLink>
            {/* Capabilities · the desktop dropdown's contents, flattened under
                a heading. A nested disclosure inside an already-scrolling
                sheet buries the links behind a second tap for no gain. */}
            <div className="space-y-3 pt-1">
              <div className="cr-nav-group-label text-[11px] font-semibold uppercase tracking-[0.14em]">
                Capabilities
              </div>
              {capabilities.map((item) => (
                <CinematicLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  aria-current={isActive(item.to) ? "page" : undefined}
                  // `flex`, not `inline-flex`: three inline-level siblings flow
                  // onto one line and space-y-* has nothing to space, so the
                  // group rendered as a single run of run-together labels.
                  className={`${mobileLinkClass(item.to)} flex items-center justify-center gap-2`}
                >
                  <item.Icon className="h-4 w-4" aria-hidden="true" />
                  {item.label}
                </CinematicLink>
              ))}
            </div>
            <CinematicLink
              to="/about"
              onClick={() => setMobileOpen(false)}
              aria-current={isActive("/about") ? "page" : undefined}
              className={mobileLinkClass("/about")}
            >
              {t("nav.about")}
            </CinematicLink>
            {/* Mirrors the desktop hierarchy: the glowing pill is Live Demo,
                and it is themed through .cr-nav-demo rather than the hardcoded
                #05e599 the Verify pill used to carry here (which stayed dark
                mint on the light sheet). */}
            <CinematicLink
              to="/demo/scanner"
              onClick={() => setMobileOpen(false)}
              aria-current={isActive("/demo/scanner") ? "page" : undefined}
              className="cr-nav-demo flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-base font-semibold"
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              {t("nav.liveDemo")}
            </CinematicLink>
            {/* These landing CTAs use CinematicLink for consistent
                hover/focus chunk preloading; navigation itself is plain
                and instant (landing routes are short-circuited). */}
            <div className="pt-4 border-t border-[#1C1C1F] space-y-3">
              {hasSession ? (
                <CinematicLink
                  to="/dash"
                  onClick={() => setMobileOpen(false)}
                  aria-current={isActive("/dash") ? "page" : undefined}
                  className={mobileLinkClass("/dash")}
                >
                  {t("nav.dashboard", "Dashboard")}
                </CinematicLink>
              ) : (
                <>
                  <CinematicLink
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    aria-current={isActive("/login") ? "page" : undefined}
                    className={mobileLinkClass("/login")}
                  >
                    {t("nav.signIn")}
                  </CinematicLink>
                  <CinematicLink
                    to="/signup"
                    onClick={() => setMobileOpen(false)}
                    className="cr-nav-cta block w-full text-center px-4 py-3 rounded-lg text-sm font-semibold"
                  >
                    {t("nav.startFree")}
                  </CinematicLink>
                </>
              )}
              {isThemed && (
                <button
                  type="button"
                  onClick={toggle}
                  className="cr-nav-toggle inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border text-sm font-medium"
                >
                  {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                  {theme === "light" ? "Dark theme" : "Light theme"}
                </button>
              )}
              <div className="pt-2 flex items-center justify-center">
                <LanguageSwitcher variant="full" />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
